import React, { useState, useEffect, useRef } from "react";
import {
  Ticket,
  Loader2,
  CheckCircle2,
  Info,
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from "lucide-react";

import TextInputComp from "../TextInputComp";
import CommonButton from "../../CommonButton";

import {
  useApplyCouponMutation,
  useLazyRemoveCouponQuery,
} from "../../../redux/actions/Customer/cartCustomer";

import { Coupon } from "../../../types/Response/CustomerResponse";

interface Props {
  availableCoupons?: Coupon[];
  onCouponSuccess?: (code: string) => void;
  onRemoveCoupon?: () => void;
  activeCouponCode?: string | null;
}

const CouponCard: React.FC<Props> = ({
  availableCoupons = [],
  onCouponSuccess,
  onRemoveCoupon,
  activeCouponCode,
}) => {
  const [couponText, setCouponText] = useState("");
  const [error, setError] = useState("");
  const [localClear, setLocalClear] = useState(false);

  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const [tooltipData, setTooltipData] = useState<{
    id: number;
    code: string;
    desc: string;
    element: HTMLElement | null;
  } | null>(null);

  const [applyCouponAction, { isLoading: isApplying }] =
    useApplyCouponMutation();

  const [triggerRemoveCoupon, { isFetching: isRemoving }] =
    useLazyRemoveCouponQuery();

  /* =========================================================
     ACTIVE COUPON
  ========================================================= */
  useEffect(() => {
    if (activeCouponCode) {
      setLocalClear(false);
    }
  }, [activeCouponCode]);

  /* =========================================================
     CLOSE TOOLTIP OUTSIDE
  ========================================================= */
  useEffect(() => {
    const outside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setTooltipData(null);
      }
    };

    document.addEventListener("mousedown", outside);

    return () => {
      document.removeEventListener("mousedown", outside);
    };
  }, []);

  /* =========================================================
     APPLY COUPON
  ========================================================= */
  const handleApplyAction = async (code: string) => {
    setError("");

    const coupon = code.trim().toUpperCase();

    if (!coupon) {
      setError("Please enter coupon code.");
      return;
    }

    try {
      await applyCouponAction({
        couponCode: coupon,
      }).unwrap();

      setCouponText("");
      setTooltipData(null);
      setLocalClear(false);

      onCouponSuccess?.(coupon);
    } catch (err: any) {
      setError(
        err?.data?.Message ||
          "Invalid Coupon Code."
      );
    }
  };

  /* =========================================================
     REMOVE COUPON
  ========================================================= */
  const handleRemoveClick = async () => {
    try {
      setLocalClear(true);

      await triggerRemoveCoupon().unwrap();

      onRemoveCoupon?.();
      setError("");
    } catch {
      setLocalClear(false);
    }
  };

  /* =========================================================
     TOOLTIP
  ========================================================= */
  const toggleTooltip = (
    e: React.MouseEvent<HTMLButtonElement>,
    coupon: Coupon
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (tooltipData?.id === coupon.couponId) {
      setTooltipData(null);
      return;
    }

    setTooltipData({
      id: coupon.couponId,
      code: coupon.couponCode,
      desc:
        coupon.description ||
        "No description available.",
      element: e.currentTarget,
    });
  };

  /* =========================================================
     SLIDER
  ========================================================= */
  const scrollSlider = (
    dir: "left" | "right"
  ) => {
    if (!sliderRef.current) return;

    sliderRef.current.scrollBy({
      left:
        dir === "left"
          ? -280
          : 280,
      behavior: "smooth",
    });

    setTooltipData(null);
  };

  /* =========================================================
     MOUSE DRAG
  ========================================================= */
  const handleMouseDown = (
    e: React.MouseEvent
  ) => {
    if (!sliderRef.current) return;

    isDown.current = true;

    startX.current =
      e.pageX -
      sliderRef.current.offsetLeft;

    scrollLeft.current =
      sliderRef.current.scrollLeft;
  };

  const handleMouseLeaveOrUp = () => {
    isDown.current = false;
  };

  const handleMouseMove = (
    e: React.MouseEvent
  ) => {
    if (
      !isDown.current ||
      !sliderRef.current
    ) {
      return;
    }

    e.preventDefault();

    const x =
      e.pageX -
      sliderRef.current.offsetLeft;

    const walk =
      (x - startX.current) * 1.4;

    sliderRef.current.scrollLeft =
      scrollLeft.current - walk;
  };

  /* =========================================================
     TOOLTIP POSITION
  ========================================================= */
  const getTooltipStyles = () => {
    if (
      !tooltipData?.element ||
      !containerRef.current
    ) {
      return {};
    }

    const target =
      tooltipData.element.getBoundingClientRect();

    const container =
      containerRef.current.getBoundingClientRect();

    const left =
      target.left -
      container.left -
      100 +
      target.width / 2;

    const bottom =
      container.bottom -
      target.top +
      6;

    return {
      left: Math.max(
        12,
        Math.min(
          left,
          container.width - 220
        )
      ),
      bottom,
    };
  };

  const hasActiveCoupon =
    activeCouponCode && !localClear;

  return (
    <div
      ref={containerRef}
      className="
        relative
        overflow-hidden
        rounded-2xl
        border border-slate-200
        bg-white

        p-5
        max-[639px]:p-2.5

        shadow-sm
        transition-all
        duration-300
      "
    >
      {/* =========================================================
          HEADER
      ========================================================= */}
      <div
        className="
          mb-3.5
          max-[639px]:mb-2.5

          flex
          items-center
          justify-between
        "
      >
        <div
          className="
            flex
            items-center
            gap-2.5
            max-[639px]:gap-1.5
          "
        >
          {/* ICON */}
          <div
            className="
              flex
              h-8
              w-8

              max-[639px]:h-6
              max-[639px]:w-6

              items-center
              justify-center
              rounded-lg

              bg-gradient-to-br
              from-[#2F5E2E]
              to-emerald-600

              shadow-xs
            "
          >
            <Ticket
              size={16}
              className="
                text-white
                max-[639px]:w-3.5
                max-[639px]:h-3.5
              "
            />
          </div>

          {/* TITLE */}
          <div>
            <h2
              className="
                flex
                items-center
                gap-1.5
                max-[639px]:gap-1

                text-sm
                max-[639px]:text-[11px]

                font-bold
                leading-tight
                text-slate-900
              "
            >
              Coupons & Offers

              <Sparkles
                size={14}
                className="
                  text-amber-500
                  fill-amber-500

                  max-[639px]:w-3
                  max-[639px]:h-3
                "
              />
            </h2>

            <p
              className="
                text-xs
                max-[639px]:text-[9px]

                leading-tight
                text-slate-500
                mt-0.5
              "
            >
              Apply a code to maximize your savings
            </p>
          </div>
        </div>

        {/* OFFER COUNT */}
        {availableCoupons.length > 0 && (
          <div
            className="
              rounded-full
              bg-emerald-50

              px-3
              max-[639px]:px-2

              py-1
              max-[639px]:py-0.5

              text-xs
              max-[639px]:text-[9px]

              font-semibold
              text-[#2F5E2E]
            "
          >
            {availableCoupons.length} Offers
          </div>
        )}
      </div>

      {!hasActiveCoupon ? (
        <>
          {/* =====================================================
              COUPON INPUT
          ===================================================== */}
          <div
            className="
              mb-3
              max-[639px]:mb-2

              rounded-xl
              border border-slate-100
              bg-white

              p-1.5
              max-[639px]:p-1

              shadow-xs
            "
          >
            <div
              className="
                flex
                items-center
                gap-2
                max-[639px]:gap-1
              "
            >
              <div className="flex-1 min-w-0">
                <TextInputComp
                  value={couponText}
                  onChangeText={(text) => {
                    setCouponText(text);

                    if (error) {
                      setError("");
                    }
                  }}
                  placeholder="Enter coupon code"
                />
              </div>

              <CommonButton
                text={
                  isApplying
                    ? ""
                    : "Apply"
                }
                icon={
                  isApplying ? (
                    <Loader2
                      size={15}
                      className="
                        animate-spin
                        max-[639px]:w-3
                        max-[639px]:h-3
                      "
                    />
                  ) : undefined
                }
                onClick={() =>
                  handleApplyAction(
                    couponText
                  )
                }
                disabled={
                  isApplying ||
                  !couponText.trim()
                }
                className="
                  h-9
                  max-[639px]:h-7

                  rounded-lg

                  bg-gradient-to-br
                  from-[#2F5E2E]
                  to-emerald-600

                  shadow-xs

                  px-4
                  max-[639px]:px-2.5

                  text-xs
                  max-[639px]:text-[10px]

                  font-semibold
                  text-white

                  transition-all
                  active:scale-95

                  disabled:opacity-50
                "
              />
            </div>

            {/* ERROR */}
            {error && (
              <div
                className="
                  mt-1.5
                  max-[639px]:mt-1

                  rounded-md
                  bg-red-50/80

                  px-2
                  py-1

                  text-xs
                  max-[639px]:text-[9px]

                  font-medium
                  text-red-500
                "
              >
                {error}
              </div>
            )}
          </div>

          {/* =====================================================
              TOOLTIP
          ===================================================== */}
          {tooltipData && (
            <div
              style={getTooltipStyles()}
              className="
                absolute
                z-50

                w-52
                max-[639px]:w-44

                rounded-xl

                bg-slate-900

                p-2.5
                max-[639px]:p-2

                text-white
                shadow-xl

                animate-in
                fade-in
                zoom-in-95
                duration-150
              "
            >
              <div className="mb-1 flex items-center justify-between">
                <p
                  className="
                    text-xs
                    max-[639px]:text-[10px]
                    font-bold
                    text-emerald-400
                  "
                >
                  {tooltipData.code}
                </p>

                <button
                  onClick={() =>
                    setTooltipData(null)
                  }
                  className="
                    text-slate-400
                    hover:text-white
                  "
                >
                  <X
                    size={13}
                    className="
                      max-[639px]:w-3
                      max-[639px]:h-3
                    "
                  />
                </button>
              </div>

              <p
                className="
                  text-xs
                  max-[639px]:text-[9px]

                  leading-relaxed
                  text-slate-300
                "
              >
                {tooltipData.desc}
              </p>

              <div
                className="
                  absolute
                  left-1/2
                  top-full
                  h-2
                  w-2
                  -translate-x-1/2
                  -translate-y-1
                  rotate-45
                  bg-slate-900
                "
              />
            </div>
          )}

          {/* =====================================================
              COUPON CAROUSEL
          ===================================================== */}
          {availableCoupons.length > 0 && (
            <div
              className="
                relative
                group/slider
                mt-2
                max-[639px]:mt-1.5
              "
            >
              {/* LEFT ARROW */}
              <button
                type="button"
                onClick={() =>
                  scrollSlider("left")
                }
                aria-label="Scroll left"
                className="
                  absolute

                  -left-3
                  max-[639px]:-left-2

                  top-1/2
                  z-20

                  flex

                  h-8
                  w-8

                  max-[639px]:h-6
                  max-[639px]:w-6

                  -translate-y-1/2

                  items-center
                  justify-center

                  rounded-full

                  border border-slate-200
                  bg-white/95

                  backdrop-blur-xs
                  shadow-md

                  transition-all
                  active:scale-95

                  opacity-90
                  hover:opacity-100
                "
              >
                <ChevronLeft
                  size={16}
                  className="
                    text-slate-700

                    max-[639px]:w-3
                    max-[639px]:h-3
                  "
                />
              </button>

              {/* RIGHT ARROW */}
              <button
                type="button"
                onClick={() =>
                  scrollSlider("right")
                }
                aria-label="Scroll right"
                className="
                  absolute

                  -right-3
                  max-[639px]:-right-2

                  top-1/2
                  z-20

                  flex

                  h-8
                  w-8

                  max-[639px]:h-6
                  max-[639px]:w-6

                  -translate-y-1/2

                  items-center
                  justify-center

                  rounded-full

                  border border-slate-200
                  bg-white/95

                  backdrop-blur-xs
                  shadow-md

                  transition-all
                  active:scale-95

                  opacity-90
                  hover:opacity-100
                "
              >
                <ChevronRight
                  size={16}
                  className="
                    text-slate-700

                    max-[639px]:w-3
                    max-[639px]:h-3
                  "
                />
              </button>

              {/* SLIDER */}
              <div
                ref={sliderRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseLeave={
                  handleMouseLeaveOrUp
                }
                onMouseUp={
                  handleMouseLeaveOrUp
                }
                className="
                  flex

                  gap-2.5
                  max-[639px]:gap-1.5

                  overflow-x-auto

                  pb-1
                  pt-1

                  [scrollbar-width:none]
                  [&::-webkit-scrollbar]:hidden
                "
              >
                {availableCoupons.map(
                  (coupon) => (
                    <div
                      key={coupon.couponId}
                      className="
                        group
                        relative

                        w-[220px]
                        max-[639px]:w-[165px]

                        shrink-0
                        overflow-hidden

                        rounded-xl
                        max-[639px]:rounded-lg

                        border
                        border-emerald-100

                        bg-gradient-to-br
                        from-white
                        via-emerald-50/20
                        to-emerald-100/30

                        p-3
                        max-[639px]:p-2

                        shadow-xs

                        transition-all
                        duration-300

                        hover:border-emerald-400
                        hover:shadow-sm
                      "
                    >
                      {/* DECORATIVE CUTMARKS */}
                      <div
                        className="
                          absolute
                          left-0
                          top-1/2

                          h-4
                          max-[639px]:h-3

                          w-1.5
                          max-[639px]:w-1

                          -translate-y-1/2

                          rounded-r-full

                          bg-white

                          border
                          border-l-0
                          border-emerald-100
                        "
                      />

                      <div
                        className="
                          absolute
                          right-0
                          top-1/2

                          h-4
                          max-[639px]:h-3

                          w-1.5
                          max-[639px]:w-1

                          -translate-y-1/2

                          rounded-l-full

                          bg-white

                          border
                          border-r-0
                          border-emerald-100
                        "
                      />

                      <div
                        className="
                          flex
                          flex-col
                          justify-between

                          h-full

                          gap-2
                          max-[639px]:gap-1.5
                        "
                      >
                        {/* TOP ROW */}
                        <div className="flex items-center justify-between gap-1">
                          {/* CODE */}
                          <span
                            className="
                              rounded

                              bg-emerald-600/10

                              px-2
                              max-[639px]:px-1.5

                              py-0.5

                              text-xs
                              max-[639px]:text-[9px]

                              font-bold
                              tracking-wider

                              text-[#2F5E2E]

                              truncate
                              max-w-[120px]
                              max-[639px]:max-w-[85px]
                            "
                          >
                            {coupon.couponCode}
                          </span>

                          {/* APPLY */}
                          <button
                            type="button"
                            disabled={isApplying}
                            onClick={() =>
                              handleApplyAction(
                                coupon.couponCode
                              )
                            }
                            className="
                              py-1
                              max-[639px]:py-0.5

                              px-2.5
                              max-[639px]:px-2

                              rounded-lg

                              bg-[#2F5E2E]

                              border
                              border-emerald-200

                              text-xs
                              max-[639px]:text-[9px]

                              font-bold
                              text-white

                              transition-all

                              hover:bg-[#2F5E2E]
                              hover:text-white
                              hover:border-transparent

                              active:scale-95

                              disabled:opacity-50

                              shrink-0
                            "
                          >
                            Apply
                          </button>
                        </div>

                        {/* BOTTOM ROW */}
                        <div
                          className="
                            flex
                            flex-row
                            justify-between
                            items-center

                            gap-2
                            max-[639px]:gap-1
                          "
                        >
                          <h3
                            className="
                              text-xs
                              max-[639px]:text-[10px]

                              font-bold
                              text-slate-800

                              line-clamp-1
                              leading-tight

                              min-w-0
                            "
                          >
                            {coupon.couponName}
                          </h3>

                          {/* INFO */}
                          <button
                            type="button"
                            onClick={(e) =>
                              toggleTooltip(
                                e,
                                coupon
                              )
                            }
                            className="
                              rounded

                              p-1
                              max-[639px]:p-0.5

                              text-slate-400

                              hover:bg-emerald-50
                              hover:text-emerald-600

                              transition

                              shrink-0
                            "
                          >
                            <Info
                              size={14}
                              className="
                                max-[639px]:w-3
                                max-[639px]:h-3
                              "
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        /* =========================================================
           ACTIVE COUPON SUCCESS STATE
        ========================================================= */
        <div
          className="
            rounded-xl

            border
            border-emerald-200

            bg-emerald-50

            p-3
            max-[639px]:p-2

            shadow-xs
          "
        >
          <div
            className="
              flex
              items-center
              justify-between

              gap-3
              max-[639px]:gap-2
            "
          >
            <div
              className="
                flex
                items-center

                gap-2.5
                max-[639px]:gap-1.5

                min-w-0
              "
            >
              {/* SUCCESS ICON */}
              <div
                className="
                  flex

                  h-7
                  w-7

                  max-[639px]:h-6
                  max-[639px]:w-6

                  shrink-0

                  items-center
                  justify-center

                  rounded-full

                  bg-emerald-600

                  text-white
                  shadow-xs
                "
              >
                <CheckCircle2
                  size={16}
                  className="
                    max-[639px]:w-3.5
                    max-[639px]:h-3.5
                  "
                />
              </div>

              <div className="min-w-0">
                <p
                  className="
                    flex
                    items-center

                    gap-1.5
                    max-[639px]:gap-1

                    text-sm
                    max-[639px]:text-[10px]

                    font-bold
                    text-slate-900
                  "
                >
                  <span className="truncate">
                    {activeCouponCode} Applied!
                  </span>

                  <Sparkles
                    size={14}
                    className="
                      shrink-0

                      max-[639px]:w-3
                      max-[639px]:h-3

                      text-amber-500
                      fill-amber-500
                    "
                  />
                </p>
              </div>
            </div>

            {/* REMOVE */}
            <button
              type="button"
              disabled={isRemoving}
              onClick={handleRemoveClick}
              className="
                shrink-0

                rounded-lg

                bg-white

                border
                border-slate-200

                px-3
                max-[639px]:px-2

                py-1.5
                max-[639px]:py-1

                text-xs
                max-[639px]:text-[9px]

                font-semibold

                text-red-500

                shadow-xs

                transition

                hover:bg-red-50
                hover:border-red-200

                active:scale-95

                disabled:opacity-50
              "
            >
              {isRemoving ? (
                <Loader2
                  size={14}
                  className="
                    animate-spin

                    max-[639px]:w-3
                    max-[639px]:h-3
                  "
                />
              ) : (
                "Remove"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponCard;